import type { IndustryProduct } from "./cms.types";
import type {
  PricingAddon,
  PricingData,
  PricingFeatureRow,
  PricingPlanRow,
} from "./pricing.types";
import { isMeteredFeature } from "./pricing-categories";

/** One production site (plant) the customer runs. Each site gets its own plan subscription. */
export type SiteScale = {
  users: number;
  workCenters: number;
  /** Accounting locations (shop areas / warehouses) inside this plant. */
  locations: number;
};

export type PlanLimits = {
  users: number;
  locations: number;
  storage: string;
};

export type MeteredLine = {
  slug: string;
  label: string;
  quantity: number;
};

export type SiteRecommendation = {
  index: number;
  scale: SiteScale;
  plan: PricingPlanRow;
  limits: PlanLimits;
  metered: MeteredLine[];
};

export type SolutionRecommendation = {
  sites: SiteRecommendation[];
  addons: { product: IndustryProduct; addon: PricingAddon | undefined }[];
  modules: IndustryProduct[];
  totals: { sites: number; users: number; locations: number; extraUsers: number; extraLocations: number };
};

export const DEFAULT_SITE: SiteScale = { users: 5, workCenters: 3, locations: 2 };
export const MAX_SITES = 8;

const clampInt = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : min;

export function normalizeSite(site: Partial<SiteScale>): SiteScale {
  return {
    users: clampInt(Number(site.users ?? DEFAULT_SITE.users), 1, 2000),
    workCenters: clampInt(Number(site.workCenters ?? DEFAULT_SITE.workCenters), 1, 500),
    locations: clampInt(Number(site.locations ?? DEFAULT_SITE.locations), 1, 200),
  };
}

/** URL form: "users-workCenters-locations" per site, sites separated by commas. */
export function encodeSites(sites: SiteScale[]): string {
  return sites.map((s) => `${s.users}-${s.workCenters}-${s.locations}`).join(",");
}

export function decodeSites(value: string | undefined): SiteScale[] {
  if (!value) return [{ ...DEFAULT_SITE }];
  const parsed = value
    .split(",")
    .slice(0, MAX_SITES)
    .map((chunk) => {
      const [users, workCenters, locations] = chunk.split("-").map((n) => Number(n));
      return normalizeSite({ users, workCenters, locations } as Partial<SiteScale>);
    });
  return parsed.length ? parsed : [{ ...DEFAULT_SITE }];
}

/** "20" / "10 000" -> number; "Let's talk" / "unlimited" -> Infinity. */
function parseLimit(value: string | undefined): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const digits = value.replace(/[^\d]/g, "");
  if (!digits) return Number.POSITIVE_INFINITY;
  return Number(digits);
}

function limitsFor(plan: PricingPlanRow, pricing: PricingData): PlanLimits {
  // Match on the untranslated English source text: group titles and feature
  // labels are localized in the CMS, so matching the display text would fail
  // on every non-English locale and silently return "no limits".
  const groupText = (g: PricingData["groups"][number]) => g.sourceTitle ?? g.title;
  const rowText = (r: PricingFeatureRow) => r.sourceLabel ?? r.label;
  const group =
    pricing.groups.find((g) => /limit/i.test(groupText(g))) ??
    pricing.groups.find((g) => g.features.some((row) => /storage locations/i.test(rowText(row))));
  const rows = group?.features ?? [];
  const find = (re: RegExp) => rows.find((row) => re.test(rowText(row)))?.values[plan.id];
  return {
    users: parseLimit(find(/^users/i)),
    locations: parseLimit(find(/storage locations/i)),
    storage: find(/storage space/i) ?? "",
  };
}

/** Smallest plan index a site needs, from limits, shop-floor size and scenario complexity. */
function planIndexFor(
  site: SiteScale,
  plans: PricingPlanRow[],
  limitsByPlan: PlanLimits[],
  challengeCount: number,
): number {
  const last = plans.length - 1;
  let index = limitsByPlan.findIndex(
    (limits) => site.users <= limits.users && site.locations <= limits.locations,
  );
  if (index < 0) index = last;

  if (site.workCenters > 30) index = Math.max(index, Math.min(2, last));
  else if (site.workCenters > 10) index = Math.max(index, Math.min(1, last));

  if (challengeCount >= 5) index = Math.max(index, Math.min(2, last));
  else if (challengeCount >= 3) index = Math.max(index, Math.min(1, last));

  return Math.min(index, last);
}

const METERED_LABELS: Record<string, string> = {
  "extra-user": "Extra users",
  "extra-storage-location": "Extra accounting locations",
};

export function recommendSolution(options: {
  pricing: PricingData;
  products: IndustryProduct[];
  selectedRefs: string[];
  sites: SiteScale[];
  /** Optional manual plan override applied to every site. */
  planSlug?: string | undefined;
}): SolutionRecommendation | null {
  const { pricing, products, selectedRefs, sites, planSlug } = options;
  const plans = pricing.plans;
  if (!plans.length || !sites.length) return null;

  const selected = products.filter((product) => selectedRefs.includes(product.product_ref));
  const limitsByPlan = plans.map((plan) => limitsFor(plan, pricing));
  const override = planSlug ? plans.find((plan) => plan.slug === planSlug) : undefined;

  const siteRecs: SiteRecommendation[] = sites.map((scale, index) => {
    const planIndex = planIndexFor(scale, plans, limitsByPlan, selected.length);
    const plan = override ?? (plans[planIndex] as PricingPlanRow);
    const limits = override ? (limitsByPlan[plans.indexOf(override)] as PlanLimits) : (limitsByPlan[planIndex] as PlanLimits);
    const metered: MeteredLine[] = [];
    const extraUsers = Number.isFinite(limits.users) ? Math.max(0, scale.users - limits.users) : 0;
    const extraLocations = Number.isFinite(limits.locations) ? Math.max(0, scale.locations - limits.locations) : 0;
    if (extraUsers > 0) metered.push({ slug: "extra-user", label: METERED_LABELS["extra-user"] as string, quantity: extraUsers });
    if (extraLocations > 0)
      metered.push({
        slug: "extra-storage-location",
        label: METERED_LABELS["extra-storage-location"] as string,
        quantity: extraLocations,
      });
    return { index, scale, plan, limits, metered };
  });

  const addonProducts = selected.filter((product) => product.product_type === "addon");
  const addons = addonProducts.map((product) => ({
    product,
    addon: pricing.addons.find((item) => item.slug === product.product_ref && !isMeteredFeature(item)),
  }));

  return {
    sites: siteRecs,
    addons,
    modules: selected.filter((product) => product.product_type === "module"),
    totals: {
      sites: siteRecs.length,
      users: sites.reduce((sum, s) => sum + s.users, 0),
      locations: sites.reduce((sum, s) => sum + s.locations, 0),
      extraUsers: siteRecs.reduce((sum, s) => sum + (s.metered.find((m) => m.slug === "extra-user")?.quantity ?? 0), 0),
      extraLocations: siteRecs.reduce(
        (sum, s) => sum + (s.metered.find((m) => m.slug === "extra-storage-location")?.quantity ?? 0),
        0,
      ),
    },
  };
}

/** Plain-text summary sent with the request form. */
export function summarizeRecommendation(
  industryLabel: string,
  recommendation: SolutionRecommendation,
): string {
  const lines = [
    "Solution finder selection:",
    `Industry: ${industryLabel}`,
    `Sites: ${recommendation.totals.sites}`,
  ];
  recommendation.sites.forEach((site) => {
    lines.push(
      `Site ${site.index + 1}: ${site.plan.name} — ${site.scale.users} users, ${site.scale.workCenters} work centers, ${site.scale.locations} accounting locations` +
        (site.metered.length ? ` (${site.metered.map((m) => `${m.label}: ${m.quantity}`).join(", ")})` : ""),
    );
  });
  if (recommendation.addons.length) {
    lines.push(`Add-ons: ${recommendation.addons.map((a) => a.addon?.name ?? a.product.name).join(", ")}`);
  }
  if (recommendation.modules.length) {
    lines.push(`Modules in focus: ${recommendation.modules.map((m) => m.name).join(", ")}`);
  }
  return lines.join("\n");
}
